// ABOUTME: Pathless layout route that guards authenticated routes.
// ABOUTME: Wraps children with context providers, sidebar, and auth redirect.

import { ShareDialogProvider } from "@/components/ShareDialogProvider";
import Sidebar from "@/components/Sidebar";
import { useAuth } from "@/contexts/AuthContext";
import { ChatProvider } from "@/contexts/ChatContext";
import { CustomerProvider } from "@/contexts/CustomerContext";
import { DocumentsProvider } from "@/contexts/DocumentsContext";
import { FoldersProvider } from "@/contexts/FoldersContext";
import { LibraryActionsProvider } from "@/contexts/LibraryActionsContext";
import { queryClient } from "@/lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  createFileRoute,
  redirect,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import { useEffect, useState } from "react";

export const Route = createFileRoute("/_authenticated")({
  beforeLoad: ({ context, location }) => {
    if (!context.auth.user) {
      throw redirect({
        to: "/login",
        search: { redirect: location.href },
      });
    }
  },
  component: AuthenticatedLayout,
});

function AuthenticatedLayout() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const pathname = useLocation({ select: (location) => location.pathname });
  const isInvestigation = pathname.startsWith("/loss-drivers");
  const [sidebarOpen, setSidebarOpen] = useState(!isInvestigation);
  useEffect(() => {
    if (isInvestigation) setSidebarOpen(false);
  }, [isInvestigation]);

  // beforeLoad doesn't re-run on context changes, so handle logout here.
  // Preserve the current URL so the user lands back where they were after
  // logging in again.
  useEffect(() => {
    if (!user) {
      const here =
        typeof window !== "undefined"
          ? window.location.pathname + window.location.search
          : undefined;
      navigate({
        to: "/login",
        search: here ? { redirect: here } : undefined,
      });
    }
  }, [user, navigate]);

  return (
    <QueryClientProvider client={queryClient}>
      <CustomerProvider>
        <DocumentsProvider>
          <FoldersProvider>
            <LibraryActionsProvider>
              <ChatProvider>
                <ShareDialogProvider>
                  <div className="flex h-dvh overflow-hidden bg-background">
                    <Sidebar
                      isOpen={sidebarOpen}
                      onToggle={() => setSidebarOpen(!sidebarOpen)}
                    />
                    <div className="flex min-w-0 flex-1 overflow-hidden">
                      <Outlet />
                    </div>
                  </div>
                </ShareDialogProvider>
              </ChatProvider>
            </LibraryActionsProvider>
          </FoldersProvider>
        </DocumentsProvider>
      </CustomerProvider>
    </QueryClientProvider>
  );
}
